import { v4 as uuidv4 } from 'uuid';

export type CreditRegistrationAction = {
  type: string;
  payload?: unknown;
  meta?: CreditRegistrationMeta;
};

export type CreditRegistrationMeta = {
  props?: {
    flowId?: string;
    ssn?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type CreditRegistrationSsnDiagnostics = {
  hasFlowId: boolean;
  hasFlowSsn: boolean;
  hasLegacyMetaSsn: boolean;
  hasLegacyPayloadSsn: boolean;
};

const registrationSsns = new Map<string, string>();

// Backstop only. The flow is cleared when registration terminates, so an entry that is still
// registered a day later means no terminating action ever fired for it.
const FLOW_TTL_MS = 24 * 60 * 60 * 1000;

const getPayloadSsn = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object' || !('ssn' in payload)) return undefined;
  return typeof payload.ssn === 'string' ? payload.ssn : undefined;
};

export const createCreditRegistrationFlow = (ssn: string): string => {
  const flowId = uuidv4();
  registrationSsns.set(flowId, ssn);
  // Captures flowId only, so an expired timer never keeps the SSN alive.
  const expiry = setTimeout(() => registrationSsns.delete(flowId), FLOW_TTL_MS);
  // Node counts a pending timer as work and will not exit for a day; RN/browsers return a plain
  // numeric handle with no unref and no idle-exit, so this is a no-op there.
  if (typeof expiry === 'object' && 'unref' in expiry) expiry.unref();
  return flowId;
};

export const getCreditRegistrationSsn = (flowId?: string): string | undefined =>
  flowId ? registrationSsns.get(flowId) : undefined;

export const clearCreditRegistrationFlow = (flowId?: string): void => {
  if (flowId) registrationSsns.delete(flowId);
};

export const getCreditRegistrationFlowId = (action: CreditRegistrationAction): string | undefined =>
  action.meta?.props?.flowId;

export const resolveCreditRegistrationSsn = (action: CreditRegistrationAction): string | undefined => {
  const { flowId, ssn: metaSsn } = action.meta?.props ?? {};
  return getCreditRegistrationSsn(flowId) ?? metaSsn ?? getPayloadSsn(action.payload);
};

export const sanitizeCreditRegistrationMeta = (meta?: CreditRegistrationMeta): CreditRegistrationMeta | undefined => {
  if (!meta?.props) return meta;

  const { ssn: _ssn, ...props } = meta.props;
  return { ...meta, props };
};

export const getCreditRegistrationSsnDiagnostics = (
  action: CreditRegistrationAction,
): CreditRegistrationSsnDiagnostics => {
  const { flowId, ssn: metaSsn } = action.meta?.props ?? {};
  return {
    hasFlowId: Boolean(flowId),
    hasFlowSsn: Boolean(getCreditRegistrationSsn(flowId)),
    hasLegacyMetaSsn: Boolean(metaSsn),
    hasLegacyPayloadSsn: Boolean(getPayloadSsn(action.payload)),
  };
};
